<template>
  <v-layout v-if="!isOverviewLoading || overview" column class="wrapper">
    <div class="page-header">
      <h1 class="page-title">{{ adminText('organizationAll.floor_plan_d5786d3d', 'Floor plan') }}</h1>
      <p class="page-description">{{ adminText('organizationAll.add_and_manage_your_subscriptions_plans_bf2e1f92', 'Add and manage your subscriptions plans.') }}</p>
    </div>

    <div class="filters-container">
      <v-text-field
        v-model="searchInput"
        :placeholder="adminText('organizationAll.search_subscribers_by_name_or_id_number_60d8a987', 'Search Subscribers by Name or ID number')"
        hide-details
        clearable
        append-icon="search"
        class="search-field boxed-input"
        @input="onSearchInput"
      />
      <v-select
        v-model="selectedAvailabilityEventId"
        :items="theaterSubscriptionEvents"
        :label="adminText('organizationAll.filter_by_event_d10c13a1', 'Filter by Event ')"
        item-text="eventName"
        item-value="eventId"
        hide-details
        clearable
        class="availability-select boxed-input"
        :disabled="theaterSubscriptionEvents.length === 0"
        @click:clear="clearAvailabilityEvent"
      />
      <v-select
        v-if="eventTemplateId"
        v-model="selectedEventId"
        :items="localizedPerformanceDates"
        :label="adminText('organizationAll.events_c5497bca', 'Events')"
        item-text="from"
        item-value="id"
        hide-details
        clearable
        class="events-select boxed-input"
        @click:clear="clearEvent"
      />
      <v-select
        v-model="selectedSeason"
        :items="seasons"
        :label="adminText('organizationAll.season_62255f56', 'Season')"
        hide-details
        :disabled="seasons.length === 0"
        class="season-select boxed-input"
      />
    </div>

    <div v-if="overviewError" role="alert" class="overview-error">
      {{ organizationText(overviewError) }}
      <button type="button" @click="fetchFloorPlanAvailabilityData">{{ adminText('organizationAll.retry_9f5cd8a2', 'Retry') }}</button>
    </div>
    <spinner v-else-if="!showFloorPlan" />
    <div v-else-if="!overview || !overview.venue">
      {{ adminText('organizationAll.nema_numeriranih_pretplata_s_tlocrtom_b1e4a8b6', 'Nema numeriranih pretplata s tlocrtom.') }}
    </div>
    <div v-else class="floor-plan-wrapper">
      <div class="order-items-container">
        <div
          v-for="orderItem in filteredOrderItems"
          :key="orderItem.id"
          class="order-item-container"
          :style="{ borderColor: orderItem.selected ? '#D9D9D9' : '#fff' }"
          @click="handleOrderItemClick(orderItem)"
        >
          <div class="color" :style="{ backgroundColor: orderItem.color }" />
          <div>
            <span class="base-text">{{ orderItem.name }}: </span>
            <span class="base-text bold-text">{{ orderItem.ticketsNumber }}</span>
          </div>
        </div>
      </div>
      <div class="floor-plan-container">
        <div class="object-container">
          <spinner v-if="coloringInProgress" />
          <object
            id="subscription-floor-plan"
            :key="objectKey"
            :data="overview.venue.imageUrl"
            type="image/svg+xml"
            style="width: 100%"
            @load="onSvgLoaded"
          />
        </div>
        <div class="total-label">
          <span class="base-text">{{ adminText('organizationAll.free_75f52718', 'Free') }} </span>
          <span class="base-text bold-text">
            {{ availableSeats.length }}/{{ audienceCapacity }}
          </span>
        </div>
        <div class="zoom-controls">
          <v-icon id="zoomIn" :size="43" :color="zoomInIconColor" style="user-select: none">
            add_circle
          </v-icon>
          <v-icon id="zoomOut" :size="43" :color="zoomOutIconColor" style="user-select: none">
            remove_circle
          </v-icon>
        </div>
      </div>
    </div>
    <div
      v-if="seatInfoVisible"
      class="seat-info-tooltip"
      :style="{ top: seatInfoPosition.y + 'px', left: seatInfoPosition.x + 'px' }"
    >
      <div>{{ adminText('organizationAll.seat_29eb32a0', 'Seat:') }} {{ hoveredSeat }}</div>
      <div>{{ adminText('organizationAll.plan_335c04da', 'Plan:') }} {{ hoveredPlanName }}</div>
      <div v-if="hoveredSeatHolderInfo">
        {{ adminText('organizationAll.subscriber_3849a5a0', 'Subscriber:') }} {{ getSubscriberName(hoveredSeatHolderInfo) }}
      </div>
    </div>
  </v-layout>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import Spinner from '@/components/shared/spinner.vue'
import {
  GET_THEATER_SUBSCRIPTION_FLOOR_PLAN_OVERVIEW,
  GET_THEATER_SUBSCRIPTION_EVENTS,
} from '@/graphql/theater-subscriptions.graphql'
import { GET_EVENTS_BY_EVENT_TEMPLATE_ID } from '@/graphql/event.graphql'
import moment from 'moment/moment'
import * as svgPanZoom from 'svg-pan-zoom'
import * as Hammer from 'hammerjs'

const SUBSCRIPTION_SEAT_COLOR = '#f5f5f5'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscriptions-floor-plan',
  components: {
    Spinner,
  },
  props: {
    eventId: String,
    eventTemplateId: String,
    organizationSlug: String,
  },
  data() {
    return {
      selectedEventId: null,
      selectedAvailabilityEventId: null,
      performanceDates: [],
      theaterSubscriptionEvents: [],
      showFloorPlan: false,
      venue: null,
      orderItems: [],
      availableSeats: [],
      disabledSeats: [],
      coloringInProgress: true,
      audienceCapacity: 0,
      seatInfoVisible: false,
      hoveredSeat: null,
      seatInfoPosition: null,
      seatHolders: [],
      hoveredSeatHolderInfo: null,
      hoveredPlanName: null,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      overview: null,
      searchInput: '',
      selectedSeason: null,
      seasons: [],
      searchDebounceTimer: null,
      objectKey: 0,
      isOverviewLoading: false,
      overviewError: null,
      overviewRequestId: 0,
      zoomInIconColor: '#0277BD',
      zoomOutIconColor: '#0277BD',
      maxZoom: 3,
      startX: null,
      startY: null,
      highlightedSeats: [],
      multiTouch: false,
      svgLoadAttempts: 0,
      seatListenersBound: false,
      boundSeatElements: [],
      zoomButtonsBound: false,
      mobileGestureListeners: [],
      touchGesture: null,
      lastGestureWasDrag: false,
      touchMoveThreshold: 6,
    }
  },
  computed: {
    localizedPerformanceDates() {
      const format = this.isAdminLocalization && this.$adminLocale() === 'en'
        ? 'DD.MM.YYYY [at] HH:mm' : 'DD.MM.YYYY [u] HH:mm'

      return this.performanceDates.map(performance => ({ ...performance, from: this.organizationDate(performance.from, format) }))
    },
    assignments() {
      return (this.overview && this.overview.assignments) || []
    },
    filteredOrderItems() {
      // Return order items with updated counts based on filtered assignments
      return this.orderItems.map(orderItem => {
        const filteredSeats = orderItem.seats.filter(seatId => {
          const assignment = this.assignments.find(a => a.seatId === seatId)
          // Only count seats that are actually taken (not released and available)

          return assignment && assignment.interactionStatus !== 'RELEASED_AND_AVAILABLE'
        })

        return {
          ...orderItem,
          ticketsNumber: filteredSeats.length,
        }
      })
    },
  },
  watch: {
    async selectedEventId(val) {
      if (!val) return
      // Reset state to avoid flashing old SVG/colors
      this.venue = null
      this.availableSeats = []
      this.disabledSeats = []
      this.orderItems = []
      this.seatHolders = []
      await this.fetchFloorPlanAvailabilityData()
    },
    async selectedAvailabilityEventId() {
      await this.fetchFloorPlanAvailabilityData()
    },
    async selectedSeason(val, oldVal) {
      // Skip the initial adoption of the backend-resolved default; that value came from the
      // response we are already rendering.
      if (oldVal === null || val === oldVal) return
      // Events are scoped to the previous season's venues. Re-select after loading this season.
      this.selectedAvailabilityEventId = null
      this.selectedEventId = null
      this.theaterSubscriptionEvents = []
      await this.fetchFloorPlanAvailabilityData()
    },
  },

  mounted() {
    this.fetchFloorPlanAvailabilityData()
  },
  beforeDestroy() {
    this.overviewRequestId += 1
    clearTimeout(this.searchDebounceTimer)
    this.removeSeatInfoListeners()
    this.destroyPanZoom()
  },
  methods: {
    resetSvgState() {
      this.removeSeatInfoListeners()
      this.removeGestureListeners()
      this.resetGestureTracking()
      this.lastGestureWasDrag = false
      this.destroyPanZoom()
      this.svgLoadAttempts = 0
      this.seatListenersBound = false
      this.boundSeatElements = []
      this.zoomButtonsBound = false
      this.coloringInProgress = true
      this.seatInfoVisible = false
      this.hoveredSeat = null
      this.hoveredSeatHolderInfo = null
      this.hoveredPlanName = null
    },
    getSvgElement() {
      return document.getElementById('subscription-floor-plan')
    },
    getSvgDocument() {
      const svgElement = this.getSvgElement()
      if (!svgElement) return null

      try {
        if (svgElement.contentDocument) {
          return svgElement.contentDocument
        }

        if (typeof svgElement.getSVGDocument === 'function') {
          return svgElement.getSVGDocument()
        }
      } catch (error) {
        return null
      }

      return null
    },
    addSeatInfoListeners(svgDoc) {
      if (!svgDoc) return

      this.removeSeatInfoListeners()

      const seatIds = new Set([
        ...this.availableSeats,
        ...this.disabledSeats,
        ...this.orderItems.flatMap(item => [
          ...(item.seats || []),
          ...(item.soldSeats || []),
          ...(item.reservedSeats || []),
        ]),
        ...((this.overview && this.overview.assignments) || []).map(a => a.seatId),
      ])

      const bindings = []
      seatIds.forEach(seatId => {
        const seatElement = svgDoc.getElementById(seatId)
        if (!seatElement) return

        seatElement.style.cursor = 'pointer'
        seatElement.style.pointerEvents = 'all'
        const enterHandler = event => this.onSeatPointerEnter(event)
        const moveHandler = event => this.onSeatPointerMove(event)
        const leaveHandler = () => this.onSeatPointerLeave()

        seatElement.addEventListener('mouseenter', enterHandler)
        seatElement.addEventListener('mousemove', moveHandler)
        seatElement.addEventListener('mouseleave', leaveHandler)

        bindings.push({ element: seatElement, enterHandler, moveHandler, leaveHandler })
      })

      this.boundSeatElements = bindings
      this.seatListenersBound = bindings.length > 0
    },
    removeSeatInfoListeners() {
      if (!this.boundSeatElements.length) return

      for (const binding of this.boundSeatElements) {
        const { element, enterHandler, moveHandler, leaveHandler } = binding
        element.removeEventListener('mouseenter', enterHandler)
        element.removeEventListener('mousemove', moveHandler)
        element.removeEventListener('mouseleave', leaveHandler)
      }

      this.boundSeatElements = []
      this.seatListenersBound = false
    },
    destroyPanZoom() {
      this.removeGestureListeners()
      this.resetGestureTracking()
      this.lastGestureWasDrag = false
      if (typeof window === 'undefined') return
      if (window.panZoom && typeof window.panZoom.destroy === 'function') {
        window.panZoom.destroy()
      }
      window.panZoom = null
    },
    registerGestureListeners(svgElement) {
      if (!svgElement) return

      this.removeGestureListeners()

      const listeners = []
      const addListener = (type, handler, options) => {
        svgElement.addEventListener(type, handler, options)
        listeners.push({ element: svgElement, type, handler, options })
      }

      const touchStartHandler = event => this.onGestureTouchStart(event)
      const touchMoveHandler = event => {
        if (event.cancelable) {
          event.preventDefault()
        }
        this.onGestureTouchMove(event)
      }
      const touchEndHandler = event => {
        this.onGestureTouchEnd(event)
        if (this.shouldIgnoreSeatSelection(event)) {
          event.stopImmediatePropagation()
          if (event.cancelable) {
            event.preventDefault()
          }

          return
        }
        this.resetGestureTracking()
      }
      const touchCancelHandler = () => this.onGestureTouchCancel()
      const pointerDownHandler = event => this.onGesturePointerDown(event)
      const pointerMoveHandler = event => this.onGesturePointerMove(event)
      const pointerUpHandler = event => {
        this.onGesturePointerUp(event)
        if (this.shouldIgnoreSeatSelection(event)) {
          event.stopImmediatePropagation()
          if (event.cancelable) {
            event.preventDefault()
          }

          return
        }
        this.resetGestureTracking()
      }
      const pointerCancelHandler = event => this.onGesturePointerCancel(event)
      const clickHandler = event => this.onGestureClickGuard(event)

      addListener('touchstart', touchStartHandler, { passive: true, capture: true })
      addListener('touchmove', touchMoveHandler, { passive: false, capture: true })
      addListener('touchend', touchEndHandler, { passive: false, capture: true })
      addListener('touchcancel', touchCancelHandler, { capture: true })
      addListener('pointerdown', pointerDownHandler, { capture: true })
      addListener('pointermove', pointerMoveHandler, { capture: true })
      addListener('pointerup', pointerUpHandler, { capture: true })
      addListener('pointercancel', pointerCancelHandler, { capture: true })
      addListener('click', clickHandler, true)

      this.mobileGestureListeners = listeners
    },
    removeGestureListeners() {
      if (!this.mobileGestureListeners.length) return

      this.mobileGestureListeners.forEach(({ element, type, handler, options }) => {
        element.removeEventListener(type, handler, options)
      })

      this.mobileGestureListeners = []
      this.touchGesture = null
      this.lastGestureWasDrag = false
    },
    onGestureTouchStart(event) {
      if (!event.touches || !event.touches.length) return
      const primaryTouch = event.touches[0]

      this.startGestureTracking({
        x: primaryTouch.clientX,
        y: primaryTouch.clientY,
        multiTouch: event.touches.length > 1,
        source: 'touch',
      })
    },
    onGestureTouchMove(event) {
      if (!event.touches || !event.touches.length) return
      const primaryTouch = event.touches[0]

      this.updateGestureTracking({
        x: primaryTouch.clientX,
        y: primaryTouch.clientY,
        multiTouch: event.touches.length > 1,
      })
    },
    onGestureTouchEnd(event) {
      const touch =
        (event.changedTouches && event.changedTouches.length && event.changedTouches[0]) || null

      if (touch) {
        this.finalizeGestureTracking({ x: touch.clientX, y: touch.clientY })
      }

      if (event.touches && event.touches.length > 0) {
        this.updateGestureTracking({ multiTouch: true })
      }
    },
    onGestureTouchCancel() {
      this.lastGestureWasDrag = true
      this.resetGestureTracking()
    },
    onGesturePointerDown(event) {
      if (event.pointerType !== 'touch') return
      if (this.touchGesture && this.touchGesture.source === 'touch') return

      this.startGestureTracking({
        x: event.clientX,
        y: event.clientY,
        multiTouch: !event.isPrimary,
        source: 'pointer',
      })
    },
    onGesturePointerMove(event) {
      if (event.pointerType !== 'touch') return
      if (!this.touchGesture || this.touchGesture.source !== 'pointer') return

      this.updateGestureTracking({
        x: event.clientX,
        y: event.clientY,
        multiTouch: !event.isPrimary,
      })
    },
    onGesturePointerUp(event) {
      if (event.pointerType !== 'touch') return
      if (!this.touchGesture || this.touchGesture.source !== 'pointer') return

      this.finalizeGestureTracking({ x: event.clientX, y: event.clientY })
    },
    onGesturePointerCancel(event) {
      if (event.pointerType !== 'touch') return
      this.lastGestureWasDrag = true
      this.resetGestureTracking()
    },
    onGestureClickGuard(event) {
      if (this.shouldIgnoreSeatSelection(event)) {
        event.stopImmediatePropagation()
        if (event.cancelable) {
          event.preventDefault()
        }

        return
      }
      this.resetGestureTracking()
    },
    startGestureTracking({ x, y, multiTouch, source }) {
      if (typeof x !== 'number' || typeof y !== 'number') return
      if (source === 'pointer' && this.touchGesture && this.touchGesture.source === 'touch') {
        return
      }

      this.touchGesture = {
        source,
        startX: x,
        startY: y,
        latestX: x,
        latestY: y,
        moved: false,
        multiTouch: Boolean(multiTouch),
      }
      this.lastGestureWasDrag = false
    },
    updateGestureTracking({ x, y, multiTouch }) {
      if (!this.touchGesture) return

      if (typeof x === 'number') {
        this.touchGesture.latestX = x
      }
      if (typeof y === 'number') {
        this.touchGesture.latestY = y
      }

      if (multiTouch) {
        this.touchGesture.multiTouch = true
      }

      if (typeof x === 'number' && typeof y === 'number') {
        const deltaX = Math.abs(x - this.touchGesture.startX)
        const deltaY = Math.abs(y - this.touchGesture.startY)

        if (deltaX > this.touchMoveThreshold || deltaY > this.touchMoveThreshold) {
          this.touchGesture.moved = true
        }
      }
    },
    finalizeGestureTracking({ x, y }) {
      this.updateGestureTracking({ x, y })
    },
    resetGestureTracking() {
      this.touchGesture = null
    },
    getEventPoint(event) {
      if (!event) return null

      if (event.changedTouches && event.changedTouches.length) {
        const touch = event.changedTouches[0]

        return { x: touch.clientX, y: touch.clientY }
      }

      if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
        return { x: event.clientX, y: event.clientY }
      }

      return null
    },
    shouldIgnoreSeatSelection(event) {
      if (!this.isMobile) return false

      if (this.touchGesture) {
        const point = this.getEventPoint(event) || {
          x: this.touchGesture.latestX,
          y: this.touchGesture.latestY,
        }

        const deltaX = Math.abs(point.x - this.touchGesture.startX)
        const deltaY = Math.abs(point.y - this.touchGesture.startY)

        if (
          this.touchGesture.multiTouch ||
          this.touchGesture.moved ||
          deltaX > this.touchMoveThreshold ||
          deltaY > this.touchMoveThreshold
        ) {
          this.lastGestureWasDrag = true
          this.resetGestureTracking()

          return true
        }
      }

      if (this.lastGestureWasDrag) {
        this.lastGestureWasDrag = false

        return true
      }

      return false
    },
    changeSeatColors() {
      const svgDoc = this.getSvgDocument()

      if (!svgDoc) {
        if (this.svgLoadAttempts < 10) {
          this.svgLoadAttempts += 1
          setTimeout(() => this.changeSeatColors(), 75)

          return
        }

        this.svgLoadAttempts = 0
        this.coloringInProgress = false

        return
      }

      this.svgLoadAttempts = 0
      this.addSeatInfoListeners(svgDoc)

      // Color available seats first
      for (const availableSeatId of this.availableSeats) {
        const circle = svgDoc.getElementById(availableSeatId)
        if (circle) {
          circle.style.fill = '#979797'
        }
      }

      // Color subscription seats based on selection and interaction status
      for (const orderItem of this.orderItems) {
        const soldSeatsIds = orderItem.seats

        for (const seatId of soldSeatsIds) {
          const circle = svgDoc.getElementById(seatId)

          if (circle) {
            // Check if this seat is actually taken (not released and available)
            const assignment = this.assignments.find(a => a.seatId === seatId)
            const isActuallyTaken =
              assignment && assignment.interactionStatus !== 'RELEASED_AND_AVAILABLE'

            if (isActuallyTaken) {
              if (this.availableSeats.includes(seatId)) {
                circle.style.stroke = orderItem.selected ? orderItem.color : SUBSCRIPTION_SEAT_COLOR
                circle.style.strokeWidth = 5
              } else {
                circle.style.fill = orderItem.selected ? orderItem.color : SUBSCRIPTION_SEAT_COLOR
              }
            } else {
              // If seat is released and available, color it as available
              circle.style.fill = '#979797'
              circle.style.stroke = '#979797'
              circle.style.strokeWidth = 1
            }
          }
        }
      }

      // Color disabled seats
      for (const disabledSeatId of this.disabledSeats) {
        const circle = svgDoc.getElementById(disabledSeatId)
        if (circle) {
          circle.style.fill = '#D9D9D9'
        }
      }

      // Add highlighting for searched seats on top of other colors.
      if (this.highlightedSeats.length > 0) {
        for (const seatId of this.highlightedSeats) {
          const circle = svgDoc.getElementById(seatId)
          if (circle) {
            circle.style.fill = '#FFD700' // Gold color for highlight
            circle.style.stroke = '#DAA520'
            circle.style.strokeWidth = '3'
          }
        }
      }

      this.coloringInProgress = false
    },
    handleOrderItemClick(filteredOrderItem) {
      // Find the original order item and toggle its selection
      const originalOrderItem = this.orderItems.find(item => item.id === filteredOrderItem.id)
      if (originalOrderItem) {
        originalOrderItem.selected = !originalOrderItem.selected
        this.changeSeatColors()
      }
    },
    async fetchTheaterSubscriptionEvents() {
      try {
        const { data } = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_EVENTS,
          variables: { organizationSlug: this.organizationSlug },
        })
        console.log('Theater subscription events data:', data)
        this.theaterSubscriptionEvents = (data.getTheaterSubscriptionEvents || []).map(
          subscriptionEvent => ({
            eventId: subscriptionEvent.eventId,
            eventName: `${subscriptionEvent.event.name} - ${moment(
              subscriptionEvent.event.from,
            ).format('DD.MM.YYYY HH:mm')}`,
          }),
        )
        console.log('Mapped theater subscription events:', this.theaterSubscriptionEvents)
      } catch (error) {
        console.error('Error fetching theater subscription events:', error)
        this.theaterSubscriptionEvents = []
      }
    },
    async fetchPerformances() {
      const { data } = await this.$apollo.query({
        query: GET_EVENTS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })
      this.performanceDates = (data.getEventsByEventTemplateId || []).map(p => ({ ...p }))
      this.selectedEventId = this.performanceDates[0].id || ''
    },

    async fetchFloorPlanAvailabilityData() {
      const requestId = ++this.overviewRequestId
      this.overviewError = null
      this.resetSvgState()
      this.showFloorPlan = false
      this.coloringInProgress = true
      this.isOverviewLoading = true

      try {
        const { data } = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_FLOOR_PLAN_OVERVIEW,
          variables: {
            organizationSlug: this.organizationSlug,
            eventId: this.selectedAvailabilityEventId || this.selectedEventId || null,
            search: this.searchInput || null,
            season: this.selectedSeason || null,
          },
        })

        if (requestId !== this.overviewRequestId) return

        if (!data || !data.getTheaterSubscriptionFloorPlanOverview) {
          this.overview = null
          this.venue = null
          this.isOverviewLoading = false
          this.showFloorPlan = true
          this.coloringInProgress = false

          return
        }

        const overview = data.getTheaterSubscriptionFloorPlanOverview
        this.seasons = overview.seasons || []
        // The backend resolves the default (the season covering today), so adopt it on first
        // load without triggering another fetch.
        if (!this.selectedSeason && overview.appliedSeason) {
          this.selectedSeason = overview.appliedSeason
        }
        this.overview = overview
        this.venue = overview.venue
        this.seatHolders = []

        // Transform subscription plans to orderItems format for compatibility
        this.orderItems = (overview.plans || []).map(plan => ({
          id: plan.id,
          name: plan.name,
          color: plan.color,
          ticketsNumber: plan.totalSeats,
          seats: (overview.assignments || [])
            .filter(assignment => assignment.planId === plan.id)
            .map(assignment => assignment.seatId),
          isSubscription: true,
          selected: false,
        }))

        // Released seats remain in assignments for status/tooltips, but are free on the selected performance.
        const assignedSeats = (overview.assignments || [])
          .filter(assignment => assignment.interactionStatus !== 'RELEASED_AND_AVAILABLE')
          .map(assignment => assignment.seatId)

        // Calculate available seats (all venue seats minus assigned seats)
        const allSeats = (overview.venue && overview.venue.seatIds) || []
        this.availableSeats = allSeats.filter(seatId => !assignedSeats.includes(seatId))
        this.disabledSeats = []
        this.audienceCapacity = allSeats.length

        if (this.searchInput) {
          const matchingSubscriptionIds = (overview.subscriptions || []).map(s => s.id)
          this.highlightedSeats = (overview.assignments || [])
            .filter(a => matchingSubscriptionIds.includes(a.subscriptionId))
            .map(a => a.seatId)
        } else {
          this.highlightedSeats = []
        }

        this.theaterSubscriptionEvents = (overview.events || []).map(event => ({
          eventId: event.id,
          eventName: event.title + ' - ' + moment(event.date).format('DD.MM.YYYY HH:mm'),
        }))

        this.showFloorPlan = true
        this.isOverviewLoading = false
        this.$nextTick(() => {
          if (requestId === this.overviewRequestId) this.changeSeatColors()
        })
      } catch (error) {
        if (requestId !== this.overviewRequestId) return
        this.overview = null
        this.venue = null
        this.orderItems = []
        this.availableSeats = []
        this.disabledSeats = []
        this.seatHolders = []
        this.overviewError = 'Floor plan could not be loaded. Try again or change the filters.'
      } finally {
        // Older requests must not change either the data or loading state of the latest one.
        if (requestId === this.overviewRequestId) {
          this.isOverviewLoading = false
          this.showFloorPlan = true
          if (!this.overview || !this.overview.venue) this.coloringInProgress = false
        }
      }
    },

    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchFloorPlanAvailabilityData()
      }, 300)
    },
    clearEvent() {
      this.selectedEventId = null
    },
    clearAvailabilityEvent() {
      this.selectedAvailabilityEventId = null
    },
    onSvgLoaded() {
      this.changeSeatColors()

      // Wait for SVG to be fully rendered before initializing pan-zoom
      this.$nextTick(() => {
        this.hammerInit()
      })
    },
    setZoomButtonsActions() {
      if (this.zoomButtonsBound || typeof window === 'undefined' || !window.panZoom) return

      const zoomInEl = document.getElementById('zoomIn')
      const zoomOutEl = document.getElementById('zoomOut')
      if (!zoomInEl || !zoomOutEl) return

      zoomInEl.addEventListener('click', () => {
        window.panZoom.zoomIn()
        this.checkZoomButtonsState(true)
      })
      zoomOutEl.addEventListener('click', () => {
        window.panZoom.zoomOut()
        this.checkZoomButtonsState(false)
      })

      this.zoomButtonsBound = true
    },
    checkZoomButtonsState(isZoomingIn) {
      if (isZoomingIn) {
        if (this.getCurrZoom() >= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#D9D9D9'
        }
        if (this.getCurrZoom() > 0.51) {
          this.zoomOutIconColor = '#0277BD'
        }
      } else {
        if (this.getCurrZoom() <= this.maxZoom - 0.1) {
          this.zoomInIconColor = '#0277BD'
        }
        if (this.getCurrZoom() <= 0.51) {
          this.zoomOutIconColor = '#D9D9D9'
        }
      }
    },
    getCurrZoom() {
      return window.panZoom.getZoom()
    },
    showSeatInfo(event) {
      const seatId = event.currentTarget ? event.currentTarget.id : event.target.id
      if (!seatId) return

      const assignment = (this.overview.assignments || []).find(a => a.seatId === seatId)
      if (!assignment) return

      // Don't show tooltip for released and available seats
      if (assignment.interactionStatus === 'RELEASED_AND_AVAILABLE') {
        this.hideSeatInfo()

        return
      }

      this.hoveredSeat = seatId
      const plan = (this.overview.plans || []).find(p => p.id === assignment.planId)
      this.hoveredPlanName = plan ? plan.name : 'N/A'
      console.log(plan)

      if (assignment) {
        // Check if subscriber data is directly on the subscription or nested
        this.hoveredSeatHolderInfo = {
          name: assignment.subscriberName,
          email: assignment.subscriberEmail,
          qrCode: assignment.qrCode,
        }
      } else {
        this.hoveredSeatHolderInfo = null
      }

      this.seatInfoVisible = true
      this.updateTooltipPosition(event)
    },
    hideSeatInfo() {
      this.seatInfoVisible = false
      this.hoveredSeat = null
      this.hoveredSeatHolderInfo = null
      this.hoveredPlanName = null
    },
    updateTooltipPosition(event) {
      if (!this.seatInfoVisible) return

      const svgContainer = this.getSvgElement()
      if (!svgContainer) return

      const pointerX = event.clientX
        ? event.clientX
        : event.changedTouches && event.changedTouches[0] && event.changedTouches[0].clientX
          ? event.changedTouches[0].clientX
          : 0
      const pointerY = event.clientY
        ? event.clientY
        : event.changedTouches && event.changedTouches[0] && event.changedTouches[0].clientY
          ? event.changedTouches[0].clientY
          : 0

      const containerRect = svgContainer.getBoundingClientRect()

      this.seatInfoPosition = {
        x: containerRect.left + pointerX,
        y: containerRect.top + pointerY + 8,
      }
    },
    getSubscriberName(assignmentInfo) {
      if (!assignmentInfo) return this.organizationText('Unknown')
      console.log('assignmentInfo', assignmentInfo)
      // Handle different possible data structures
      const firstName =
        assignmentInfo.subscriberName ||
        (assignmentInfo.name && assignmentInfo.name.split(' ')[0]) ||
        ''
      const lastName =
        assignmentInfo.subscriberName ||
        (assignmentInfo.name && assignmentInfo.name.split(' ').slice(1).join(' ')) ||
        ''

      if (firstName && lastName) {
        return `${firstName} ${lastName}`
      } else if (assignmentInfo.name) {
        return assignmentInfo.name
      } else if (firstName) {
        return firstName
      } else {
        return this.organizationText('Unknown')
      }
    },
    onSeatPointerEnter(event) {
      this.showSeatInfo(event)
    },
    onSeatPointerMove(event) {
      if (this.seatInfoVisible) {
        this.updateTooltipPosition(event)
      }
    },
    onSeatPointerLeave() {
      this.hideSeatInfo()
    },
    hammerInit: function () {
      const component = this
      this.destroyPanZoom()

      const svgElement = this.getSvgElement()
      if (!svgElement) return

      // Check if SVG has valid dimensions
      const rect = svgElement.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        // Retry after a short delay if SVG dimensions are not ready
        setTimeout(() => this.hammerInit(), 100)

        return
      }

      let eventsHandler
      eventsHandler = {
        haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
        init: function (options) {
          let instance = options.instance
          let initialScale = 1
          let pannedX = 0
          let pannedY = 0
          // Init Hammer
          // Listen only for pointer and touch events
          this.hammer = Hammer(options.svgElement, {
            inputClass: Hammer.SUPPORT_POINTER_EVENTS
              ? Hammer.PointerEventInput
              : Hammer.TouchInput,
          })
          // Enable pinch
          this.hammer.get('pinch').set({
            enable: true,
          })
          // Handle double tap
          this.hammer.on('doubletap', function () {
            instance.zoomIn()
          })
          // Handle pan
          this.hammer.on('panstart panmove', function (ev) {
            // On pan start reset panned variables
            if (ev.type === 'panstart') {
              pannedX = 0
              pannedY = 0
            }
            // Pan only the difference
            instance.panBy({
              x: ev.deltaX - pannedX,
              y: ev.deltaY - pannedY,
            })
            pannedX = ev.deltaX
            pannedY = ev.deltaY
          })
          // Handle pinch
          this.hammer.on('pinchstart pinchmove', function (ev) {
            // On pinch start remember initial zoom
            if (ev.type === 'pinchstart') {
              initialScale = instance.getZoom()
              instance.zoomAtPoint(initialScale * ev.scale, {
                x: ev.center.x,
                y: ev.center.y,
              })
            }
            instance.zoomAtPoint(initialScale * ev.scale, {
              x: ev.center.x,
              y: ev.center.y,
            })
          })
          component.registerGestureListeners(options.svgElement)
        },
        destroy: function () {
          component.removeGestureListeners()
          this.hammer.destroy()
        },
      }
      let maxZoom = this.isMobile ? 5 : 3
      if (
        [
          'Arena Zagreb',
          'Bundek - Baš Naš',
          'Arena Zagreb 2',
          'Arena Zagreb - parter extended',
          'Arena Zagreb - parter A/B',
        ].includes(this.venue.name)
      ) {
        if (this.isMobile) {
          maxZoom = 35
        } else {
          maxZoom = 7
        }
      }

      try {
        // Expose to window namespace for testing purposes
        window.panZoom = svgPanZoom('#subscription-floor-plan', {
          zoomEnabled: true,
          controlIconsEnabled: false,
          maxZoom,
          fit: 2,
          center: 1,
          customEventsHandler: eventsHandler,
        })
        this.setZoomButtonsActions()
      } catch (error) {
        console.warn('Failed to initialize SVG pan-zoom:', error)
        // Retry after a short delay
        setTimeout(() => this.hammerInit(), 200)
      }
    },
  },
}
</script>

<style scoped>
.wrapper {
  max-width: 1033px;
}

@media screen and (max-width: 800px) {
  .wrapper {
    margin-top: 1.5rem;
  }
}

.title-screen {
  font-family: 'Source Sans Pro';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: #1f2933;
  margin-bottom: 1.5rem;
}

.filters-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  max-width: 1000px;
}

.search-field {
  flex: 1;
  max-width: 400px;
}

.availability-select {
  flex: 1;
  max-width: 400px;
}

.events-select {
  flex: 1;
  max-width: 400px;
}

.floor-plan-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 0.75rem;
  border: 1px solid #d9d9d9;
}

.order-items-container {
  display: flex;
  padding: 1rem 1.5rem;
  align-items: center;
  gap: 1.5rem;
  align-self: stretch;
  border-bottom: 1px solid #d9d9d9;
  overflow: hidden;
  flex-wrap: wrap;
}

.order-item-container {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  gap: 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #fff;
}

.order-item-container:hover {
  cursor: pointer;
}

.color {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 10000px;
}

.base-text {
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.bold-text {
  font-weight: 700;
}

.floor-plan-container {
  padding: 3rem;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.object-container {
  overflow: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.object-container::-webkit-scrollbar {
  display: none;
}

.total-label {
  position: absolute;
  left: 1.5rem;
  bottom: 1.5rem;
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1;
}
.seat-info-tooltip {
  position: fixed;
  background-color: #333;
  color: #fff;
  border: 1px solid #000;
  border-radius: 4px;
  padding: 10px;
  z-index: 1000;
  pointer-events: none;
  font-family: 'Source Sans Pro', sans-serif;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transform: translateX(-50%);
}

.page-header {
  margin-bottom: 1rem;
}

.page-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 0.5rem;
}

.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
  margin-bottom: 1rem;
}

.filters-container {
  margin-bottom: 2.5rem;
  gap: 1rem;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  align-items: center;
}

.search-field {
  flex: 1;
  margin-right: 0.5rem;
}

.availability-select,
.events-select {
  flex: 1;
  margin-left: 0.5rem;
}

/* Boxed input styling for Vuetify 1.2.5 */
.boxed-input {
  border: 1px solid #ccc !important;
  border-radius: 4px !important;
  background-color: #fff !important;
  padding: 2px 12px !important;
  height: 50px !important;
  min-height: 50px !important;
  max-height: 50px !important;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.season-select {
  max-width: 154px;
  flex: 0 0 154px;
}

.boxed-input >>> .v-select__selection {
  max-height: 50px !important;
  overflow: hidden !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  text-overflow: ellipsis !important;
  line-height: 1.2 !important;
}

.boxed-input >>> .v-select__selection .v-select__selection--comma {
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  text-overflow: ellipsis !important;
  overflow: hidden !important;
  max-height: 2.4em !important;
  line-height: 1.2 !important;
}

.boxed-input >>> .v-input__slot {
  border: none !important;
  box-shadow: none !important;
  background-color: transparent !important;
  height: 46px !important;
  min-height: 46px !important;
  max-height: 46px !important;
  display: flex !important;
  align-items: center !important;
}

.boxed-input >>> .v-input__slot:before {
  border: none !important;
}

.boxed-input >>> .v-input__slot:after {
  border: none !important;
}

.boxed-input >>> .v-input__append-inner,
.boxed-input >>> .v-input__prepend-inner {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 24px !important;
  height: 24px !important;
  margin: auto !important;
  padding: 0 12px !important;
}

.boxed-input >>> .v-input__icon {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.boxed-input >>> .v-input__icon .v-icon {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.boxed-input >>> .v-input__append-inner .v-icon,
.boxed-input >>> .v-input__prepend-inner .v-icon {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.boxed-input >>> .v-input__control {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
}

.boxed-input >>> .v-label {
  color: #666 !important;
  background-color: #fff !important;
  padding: 0 4px !important;
}

@media screen and (max-width: 600px) {
  .order-items-container {
    padding: 0.75rem 1rem;
    gap: 1rem;
  }

  .floor-plan-container {
    padding: 1.75rem 1rem;
  }

  .total-label {
    left: 1rem;
    bottom: 1rem;
  }

  .zoom-controls {
    top: 1rem;
    right: 1rem;
  }
}
</style>
